/**
 * Application Routes Constants
 * Centralized route paths to avoid hardcoded strings throughout the application
 * Following Angular best practices for maintainable routing
 */

/**
 * Main application routes
 */
export const APP_ROUTES = {
  HOME: '',
  OFFERS: 'offers',
  MENUS: 'menus',
  PLACES: 'places',
  LIKED_OFFERS: 'liked-offers',
  SUBMIT_OFFER: 'submit-offer',
  MY_SUBMISSIONS: 'my-submissions',
  SETTINGS: 'settings',
  ADMIN: 'admin',
  AUTH: 'auth',
  NOT_FOUND: '**',
} as const;

/**
 * Auth module routes
 */
export const AUTH_ROUTES = {
  BASE: 'auth',
  LOGIN: 'login',
  REGISTER: 'register',
  FORGOT_PASSWORD: 'forgot-password',
  RESET_PASSWORD: 'reset-password',
} as const;

/**
 * Admin module routes
 */
export const ADMIN_ROUTES = {
  BASE: 'admin',
  DASHBOARD: 'dashboard',
  HOME: 'home',
  OFFERS: 'offers',
  MENUS: 'menus',
  RESTAURANTS: 'restaurants',
  BRANDS: 'brands',
  CATEGORIES: 'categories',
  LOCATIONS: 'locations',
  USERS: 'users',
  EXTRACTIONS: 'extractions',
  REQUESTS: 'requests',
  STATISTICS: 'statistics',
  USAGE_TRACKING: 'usage-tracking',
  DATABASE: 'database',
  SUBMISSIONS: 'submissions',
  RAFFLES: 'raffles',
} as const;

/**
 * Settings module routes
 */
export const SETTINGS_ROUTES = {
  BASE: 'settings',
  PROFILE: 'profile',
  NOTIFICATIONS: 'notifications',
  PRIVACY: 'privacy',
  LEGAL: 'legal',
} as const;

/**
 * Places module routes
 */
export const PLACES_ROUTES = {
  BASE: 'places',
  DETAIL: ':id',
  OFFERS: ':id/offers',
} as const;

/**
 * Menus module routes
 */
export const MENUS_ROUTES = {
  BASE: 'menus',
  DETAIL: ':id',
} as const;

/**
 * Offers module routes
 */
export const OFFERS_ROUTES = {
  BASE: 'offers',
  DETAIL: ':id',
} as const;

/**
 * Liked Offers module routes
 */
export const LIKED_OFFERS_ROUTES = {
  BASE: 'liked-offers',
  RECOMMENDATIONS: 'recommendations',
} as const;

/**
 * Helper class to build full route paths
 * Provides type-safe route building with proper formatting
 */
export class RouteBuilder {
  private static readonly SEPARATOR = '/';

  /**
   * Build auth route path
   */
  static auth(route?: keyof typeof AUTH_ROUTES): string {
    if (!route || route === 'BASE') {
      return `${this.SEPARATOR}${AUTH_ROUTES.BASE}`;
    }
    return `${this.SEPARATOR}${AUTH_ROUTES.BASE}${this.SEPARATOR}${AUTH_ROUTES[route]}`;
  }

  /**
   * Build admin route path
   */
  static admin(route?: keyof typeof ADMIN_ROUTES): string {
    if (!route || route === 'BASE') {
      return `${this.SEPARATOR}${ADMIN_ROUTES.BASE}`;
    }
    return `${this.SEPARATOR}${ADMIN_ROUTES.BASE}${this.SEPARATOR}${ADMIN_ROUTES[route]}`;
  }

  /**
   * Build settings route path
   */
  static settings(route?: keyof typeof SETTINGS_ROUTES): string {
    if (!route || route === 'BASE') {
      return `${this.SEPARATOR}${SETTINGS_ROUTES.BASE}`;
    }
    return `${this.SEPARATOR}${SETTINGS_ROUTES.BASE}${this.SEPARATOR}${SETTINGS_ROUTES[route]}`;
  }

  /**
   * Build places route path
   */
  static places(id?: string): string {
    if (!id) {
      return `${this.SEPARATOR}${PLACES_ROUTES.BASE}`;
    }
    return `${this.SEPARATOR}${PLACES_ROUTES.BASE}${this.SEPARATOR}${id}`;
  }

  /**
   * Build places offers route path
   */
  static placesOffers(id: string): string {
    return `${this.SEPARATOR}${PLACES_ROUTES.BASE}${this.SEPARATOR}${id}${this.SEPARATOR}offers`;
  }

  /**
   * Build menus route path
   */
  static menus(id?: string): string {
    if (!id) {
      return `${this.SEPARATOR}${MENUS_ROUTES.BASE}`;
    }
    return `${this.SEPARATOR}${MENUS_ROUTES.BASE}${this.SEPARATOR}${id}`;
  }

  /**
   * Build offers route path
   */
  static offers(id?: string): string {
    if (!id) {
      return `${this.SEPARATOR}${OFFERS_ROUTES.BASE}`;
    }
    return `${this.SEPARATOR}${OFFERS_ROUTES.BASE}${this.SEPARATOR}${id}`;
  }

  /**
   * Build submit offer route path
   */
  static submitOffer(): string {
    return `${this.SEPARATOR}${APP_ROUTES.SUBMIT_OFFER}`;
  }

  static mySubmissions(): string {
    return `${this.SEPARATOR}${APP_ROUTES.MY_SUBMISSIONS}`;
  }

  /**
   * Build liked offers route path
   */
  static likedOffers(subRoute?: keyof typeof LIKED_OFFERS_ROUTES): string {
    if (!subRoute || subRoute === 'BASE') {
      return `${this.SEPARATOR}${LIKED_OFFERS_ROUTES.BASE}`;
    }
    return `${this.SEPARATOR}${LIKED_OFFERS_ROUTES.BASE}${this.SEPARATOR}${LIKED_OFFERS_ROUTES[subRoute]}`;
  }

  /**
   * Build home/root route path
   */
  static home(): string {
    return this.SEPARATOR;
  }
}

/**
 * Type-safe route paths enum for common navigation
 * Use these constants throughout the application
 */
export enum AppRoutePath {
  HOME = '/',
  OFFERS = '/offers',
  MENUS = '/menus',
  PLACES = '/places',
  LIKED_OFFERS = '/liked-offers',
  SUBMIT_OFFER = '/submit-offer',
  MY_SUBMISSIONS = '/my-submissions',
  SETTINGS = '/settings',
  ADMIN = '/admin',
  AUTH_LOGIN = '/auth/login',
  AUTH_REGISTER = '/auth/register',
  ADMIN_DASHBOARD = '/admin/dashboard',
}

/**
 * Route parameter names
 * Use for accessing route parameters consistently
 */
export const ROUTE_PARAMS = {
  ID: 'id',
  USER_ID: 'userId',
  OFFER_ID: 'offerId',
  MENU_ID: 'menuId',
  PLACE_ID: 'placeId',
} as const;

/**
 * Query parameter names
 * Use for accessing query parameters consistently
 */
export const QUERY_PARAMS = {
  REDIRECT: 'redirect',
  CATEGORY: 'category',
  SEARCH: 'search',
  PAGE: 'page',
  LIMIT: 'limit',
  SORT: 'sort',
  FILTER: 'filter',
} as const;

/**
 * Helper function to get route config for navigation
 * Useful for guards and interceptors
 */
export function getDefaultRouteForRole(
  isAdmin: boolean,
  isAuthenticated: boolean
): string {
  if (!isAuthenticated) {
    // Redirect to offers page (users can use login modal)
    return RouteBuilder.offers();
  }

  if (isAdmin) {
    return RouteBuilder.admin('DASHBOARD');
  }

  return RouteBuilder.offers();
}
